import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import { downloadBlobGet, fetchTextGet, getRequest } from '../../../api/ApiController';
import { ReactTabulator, reactFormatter } from 'react-tabulator';
import '../../../assets/css/style.css';
import '../../../assets/css/iconsmenu.css';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import UtilContext from '../../../components/UtilContext';
import AuthContext from '../../../components/AuthContext';
import { LocaleContext } from '../../../components/LocaleContext';
import {
  AddIcon,
  EditIcon,
  PrintIcon,
  SearchIcon,
  ExportIcon,
  VerticalLineIcon,
  BackToFirstIcon,
  BackIcon,
  ForwardIcon,
  ForwardToLastIcon,
  FavoriteIcon,
  PriceIcon,
  EditRowIcon,
  RefreshIcon,
} from '../../svg/svgIcons';
import { MdSearch, MdSearchOff } from 'react-icons/md';
import { PanelMenu } from 'primereact/panelmenu';
import { Checkbox } from 'primereact/checkbox';
import IntersectionObserverWrapper from '../../../components/toolbar/IntersectionObserverWrap';
import { OverflowMenuProvider } from '../../../components/toolbar/OverflowMenu';
import { scrollToFirst as scrollToFirstUtil, scrollUp as scrollUpUtil, scrollDown as scrollDownUtil, scrollToLast as scrollToLastUtil } from '../../../utils/tabulatorScroll';
import { getEffectiveMask } from '../../../utils/effectiveMask';
import { applyUserMaskRowToTabulator, cleanSorters, safeParse } from '../../../utils/tabulatorProgressiveLoading';
import { getTabulatorPersistLayoutEvents } from '../../../utils/tabulatorPersistLayout';
import { getTabulatorLayoutForStandardMask } from '../../../utils/tabulatorLayoutUtils';
import { inputOnEnterHeaderFilter } from '../../../utils/inputOnEnterHeaderFilter';
import { tabulatorColumnMinWidthFromTitle } from '../../../utils/tabulatorColumnMinWidth';
import PrintOptionsDialog from '../../../components/PrintOptionsDialog';
import { buildExportQueryStringFromParams, openPrintHtmlWindow, triggerBlobDownload } from '../../../utils/serviceTaskExport';
import { EXPORT_DOCUMENT_TITLE_KEYS, xlsxDownloadFilename } from '../../../utils/exportExcelNaming';
import { getCurrency } from '../../../utils/moduleSettings';
import { formatCurrency } from '../../../utils/currencyUtils';
import { applyTabulatorRuntimeI18n } from '../../../utils/tabulatorRuntimeI18n';


const PAGE_ID = 'OtkCompanyStatisticsPeriodList';

const DEFAULT_COLUMNS_VISIBLE_PERIOD = Object.freeze({
  period: true,
  year: true,
  month: true,
  records_count: true,
  companies_count: true,
  total_revenue: true,
  total_claim_costs: true,
  total_orders: true,
  total_items: true,
});

const PERIOD_MASK_FIELD_I18N = Object.freeze({
  period: 'OtkCompanyStatisticsPeriodList.Column.Period',
  year: 'OtkCompanyStatisticsPeriodList.Column.Year',
  month: 'OtkCompanyStatisticsPeriodList.Column.Month',
  records_count: 'OtkCompanyStatisticsPeriodList.Column.RecordsCount',
  companies_count: 'OtkCompanyStatisticsPeriodList.Column.CompaniesCount',
  total_revenue: 'OtkCompanyStatisticsPeriodList.Column.TotalRevenue',
  total_claim_costs: 'OtkCompanyStatisticsPeriodList.Column.ClaimCosts',
  total_orders: 'OtkCompanyStatisticsPeriodList.Column.TotalOrders',
  total_items: 'OtkCompanyStatisticsPeriodList.Column.TotalItems',
});

const OTK_STATS_PERIOD_COLUMN_I18N_KEYS = PERIOD_MASK_FIELD_I18N;

function mergeVisibility(saved) {
  const merged = { ...DEFAULT_COLUMNS_VISIBLE_PERIOD };
  if (saved && typeof saved === 'object') {
    Object.keys(DEFAULT_COLUMNS_VISIBLE_PERIOD).forEach((k) => {
      if (Object.prototype.hasOwnProperty.call(saved, k)) merged[k] = saved[k] !== false;
    });
  }
  if (!Object.values(merged).some(Boolean)) return { ...DEFAULT_COLUMNS_VISIBLE_PERIOD };
  return merged;
}

function normalizePeriodColumnVisibilityForSave(stateObj) {
  const out = {};
  for (const field of Object.keys(DEFAULT_COLUMNS_VISIBLE_PERIOD)) out[field] = stateObj?.[field] !== false;
  return out;
}

function columnsFromTabulatorForStandardMaskSave(raw) {
  const out = {};
  for (const field of Object.keys(DEFAULT_COLUMNS_VISIBLE_PERIOD)) out[field] = raw?.[field] !== false;
  return out;
}

function clearHeaderFilterForField(table, field) {
  if (!table || field == null) return;
  try { table.setHeaderFilterValue?.(field, '', true); } catch (_) {}
}

function applyDataColumnVisibilityToTable(table, visibilityMap) {
  if (!table) return;
  for (const f of Object.keys(DEFAULT_COLUMNS_VISIBLE_PERIOD)) {
    if (visibilityMap[f] === false) {
      clearHeaderFilterForField(table, f);
      table.hideColumn?.(f);
    } else {
      table.showColumn?.(f);
    }
  }
  setTimeout(() => table.redraw?.(true), 0);
}

function countVisibleDataColumns(vis) {
  if (!vis || typeof vis !== 'object') return 0;
  return Object.keys(DEFAULT_COLUMNS_VISIBLE_PERIOD).filter((k) => vis[k] !== false).length;
}

const normalizeText = (value) => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export default function OtkCompanyStatisticsPeriodList() {
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { t, locale } = useContext(LocaleContext);
  const {
    setDialogText,
    setVisible,
    openFavoriteDialog,
    toast,
    setFastView,
    setUserMaskFilter,
    setStandardMaskFilter,
    openChosenNotificationPreferences,
  } = useContext(UtilContext);

  const ref = useRef(null);
  const overlayRef = useRef(null);
  const isRestoringRef = useRef(false);
  const submittedSearchValueRef = useRef('');

  const [editIcon, setEditIcon] = useState(true);
  const [visibleSearch, setVisibleSearch] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchIconColor, setSearchIconColor] = useState('');
  const [maskVisible, setMaskVisible] = useState(false);
  const [columnsVisible, setColumnsVisible] = useState({ ...DEFAULT_COLUMNS_VISIBLE_PERIOD });
  const [expandedKeys, setExpandedKeys] = useState({});
  const [printChoiceVisible, setPrintChoiceVisible] = useState(false);
  const [printOrientationChoice, setPrintOrientationChoice] = useState('landscape');
  const [printLayoutChoice, setPrintLayoutChoice] = useState('branded');

  const getTable = useCallback(() => ref.current?.table ?? ref.current, []);

  const canSetStandardMask = user?.permissions?.includes('set standard mask');
  const hasApplicableStandardMask = useMemo(() => {
    const row = (state.standard_mask_filters || []).find((m) => m.page === PAGE_ID);
    if (!row) return false;
    const attributes = safeParse(row.attributes, []);
    if (!Array.isArray(attributes) || attributes.length <= 1) return false;
    const cols = safeParse(row.columns, {});
    const merged = mergeVisibility(cols && typeof cols === 'object' ? cols : {});
    return Object.values(merged).some(Boolean);
  }, [state.standard_mask_filters]);

  const currencySymbol = getCurrency(state.module_settings || []);

  const monthLabel = useCallback((m) => t(`OtkCompanyStatisticsPeriodList.Month.${Number(m)}`) || String(m), [t]);

  const periods = useMemo(() => {
    const map = new Map();
    (state.company_statistics || []).forEach((s) => {
      const y = Number(s.year);
      const m = Number(s.month);
      if (!y || !m) return;
      const key = `${y}-${m}`;
      if (!map.has(key)) {
        map.set(key, {
          id: key,
          year: y,
          month: m,
          period: `${monthLabel(m)} ${y}`,
          records_count: 0,
          companies_count_set: new Set(),
          total_revenue: 0,
          total_claim_costs: 0,
          total_orders: 0,
          total_items: 0,
        });
      }
      const row = map.get(key);
      row.records_count += 1;
      const rev = Number(s.monthly_revenue ?? 0) || 0;
      const costs = Number(s.monthly_claim_costs ?? 0) || 0;
      const ord = Number(s.orders_count ?? 0) || 0;
      const itm = Number(s.items_count ?? 0) || 0;
      row.total_revenue += rev;
      row.total_claim_costs += costs;
      row.total_orders += ord;
      row.total_items += itm;
      if (s.company_id != null) row.companies_count_set.add(String(s.company_id));
    });

    return Array.from(map.values())
      .map((p) => ({
        id: p.id,
        year: p.year,
        month: p.month,
        period: p.period,
        records_count: p.records_count,
        companies_count: p.companies_count_set.size,
        total_revenue: p.total_revenue,
        total_claim_costs: p.total_claim_costs,
        total_orders: p.total_orders,
        total_items: p.total_items,
      }))
      .sort((a, b) => (b.year - a.year) || (b.month - a.month));
  }, [monthLabel, state.company_statistics]);

  const applyToolbarSearchRowFilter = useCallback(() => {
    const table = getTable();
    if (!table) return;
    const needle = normalizeText(String(submittedSearchValueRef.current ?? '').trim());
    if (needle === '') {
      table.clearFilter(true);
      return;
    }
    table.setFilter((data) => normalizeText(JSON.stringify(data)).includes(needle));
  }, [getTable]);

  useEffect(() => {
    if (normalizeText(String(submittedSearchValueRef.current ?? '').trim()) === '') return;
    applyToolbarSearchRowFilter();
  }, [periods, applyToolbarSearchRowFilter]);

  const reloadListData = useCallback(() => {
    getRequest('/api/company_statistics')
      .then((response) => {
        if (response?.data) dispatch({ type: 'load_company_statistics', newCompanyStatistics: response.data });
      })
      .catch(() => {});
  }, [dispatch]);

  const onEditRow = useCallback((periodRow) => {
    dispatch({
      type: 'create_new_tab', name: t('OtkCompanyStatisticsPeriodList.Tab.ByPeriod'), translationKey: 'OtkCompanyStatisticsPeriodList.Tab.ByPeriod', legacyLabel: t('OtkCompanyStatisticsPeriodList.Tab.ByPeriod'),
      page: 'OtkCompanyStatisticsPeriodAdd',
      props: { year: periodRow.year, month: periodRow.month },
      mask: 'gset023',
    });
  }, [dispatch, t]);

  const openEditFromRow = useCallback((rowData) => {
    if (!rowData) return;
    onEditRow(rowData);
  }, [onEditRow]);

  function EditRowFormatter(props) {
    const rowData = props.cell.getRow().getData();
    return (
      <div className="flex items-center justify-center w-full h-full">
        <button type="button" onClick={() => openEditFromRow(rowData)}><EditRowIcon /></button>
      </div>
    );
  }

  const onSelectRow = useCallback((data) => {
    const rows = Array.isArray(data) ? data : [];
    const canEdit = user?.permissions?.includes('edit settings');
    setEditIcon(!canEdit || rows.length !== 1);
  }, [user?.permissions]);

  const textHeaderFilter = useCallback((headerValue, rowValue) => {
    if (!headerValue) return true;
    return normalizeText(rowValue).includes(normalizeText(headerValue));
  }, []);

  const columns = useMemo(() => {
    const colTitle = (key) => t(key);
    const periodTitle = colTitle('OtkCompanyStatisticsPeriodList.Column.Period');
    const revenueTitle = `${colTitle('OtkCompanyStatisticsPeriodList.Column.TotalRevenue')} (${currencySymbol})`;
    const claimTitle = `${colTitle('OtkCompanyStatisticsPeriodList.Column.ClaimCosts')} (${currencySymbol})`;
    return [
      { field: 'rowSelection', formatter: 'rowSelection', titleFormatter: 'rowSelection', hozAlign: 'center', width: 44, headerSort: false, resizable: false, frozen: true, print: false, download: false },
      { title: '', field: 'editor', hozAlign: 'center', width: 44, headerSort: false, formatter: reactFormatter(<EditRowFormatter />), resizable: false, frozen: true, print: false, download: false },
      { title: periodTitle, field: 'period', hozAlign: 'left', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(periodTitle), widthGrow: 1 },
      { title: colTitle('OtkCompanyStatisticsPeriodList.Column.Year'), field: 'year', hozAlign: 'left', sorter: 'number', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(colTitle('OtkCompanyStatisticsPeriodList.Column.Year')), widthGrow: 0.5 },
      { title: colTitle('OtkCompanyStatisticsPeriodList.Column.Month'), field: 'month', hozAlign: 'left', sorter: 'number', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(colTitle('OtkCompanyStatisticsPeriodList.Column.Month')), widthGrow: 0.5 },
      { title: colTitle('OtkCompanyStatisticsPeriodList.Column.RecordsCount'), field: 'records_count', hozAlign: 'left', sorter: 'number', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(colTitle('OtkCompanyStatisticsPeriodList.Column.RecordsCount')), widthGrow: 0.6 },
      { title: colTitle('OtkCompanyStatisticsPeriodList.Column.CompaniesCount'), field: 'companies_count', hozAlign: 'left', sorter: 'number', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(colTitle('OtkCompanyStatisticsPeriodList.Column.CompaniesCount')), widthGrow: 0.6 },
      { title: revenueTitle, field: 'total_revenue', hozAlign: 'left', sorter: 'number', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(revenueTitle), widthGrow: 1, formatter: (cell) => formatCurrency(cell.getValue(), currencySymbol) },
      { title: claimTitle, field: 'total_claim_costs', hozAlign: 'left', sorter: 'number', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(claimTitle), widthGrow: 1, formatter: (cell) => formatCurrency(cell.getValue(), currencySymbol) },
      { title: colTitle('OtkCompanyStatisticsPeriodList.Column.TotalOrders'), field: 'total_orders', hozAlign: 'left', sorter: 'number', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(colTitle('OtkCompanyStatisticsPeriodList.Column.TotalOrders')), widthGrow: 0.8 },
      { title: colTitle('OtkCompanyStatisticsPeriodList.Column.TotalItems'), field: 'total_items', hozAlign: 'left', sorter: 'number', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(colTitle('OtkCompanyStatisticsPeriodList.Column.TotalItems')), widthGrow: 0.8 },
    ];
  }, [currencySymbol, openEditFromRow, t, textHeaderFilter]);

  useEffect(() => {
    const table = getTable();
    if (!table?.getColumns) return;
    applyTabulatorRuntimeI18n(table, t, {
      columnTitleKeys: OTK_STATS_PERIOD_COLUMN_I18N_KEYS,
      placeholder: t('Common.NoData'),
      reformatRows: true,
    });
  }, [locale, t, getTable]);


  const getMaskColumnLabel = useCallback((field) => {
    const key = PERIOD_MASK_FIELD_I18N[field];
    if (field === 'total_revenue' || field === 'total_claim_costs') {
      const base = key ? t(key) : field;
      return `${base} (${currencySymbol})`;
    }
    return key ? t(key) : field;
  }, [currencySymbol, t]);

  const getPrintColumnsOrder = useCallback(() => {
    const table = getTable();
    const allowed = Object.keys(DEFAULT_COLUMNS_VISIBLE_PERIOD);
    const fallback = () => allowed.filter((f) => columnsVisible[f] !== false);
    if (!table?.getColumns) return fallback();
    const allowedSet = new Set(allowed);
    const out = [];
    const seen = new Set();
    table.getColumns().forEach((col) => {
      const field = col.getField?.() ?? col.getDefinition?.()?.field;
      if (!field || !allowedSet.has(field)) return;
      if (columnsVisible[field] === false || seen.has(field)) return;
      seen.add(field);
      out.push(field);
    });
    return out.length > 0 ? out : fallback();
  }, [columnsVisible, getTable]);

  const parseHeaderFiltersToParams = useCallback(() => {
    const hf = getTable()?.getHeaderFilters?.() ?? [];
    const out = {};
    hf.forEach((f) => {
      if (!f?.field) return;
      const v = f.value;
      if (v == null || String(v).trim() === '') return;
      out[`filter_${f.field}`] = v;
    });
    return out;
  }, [getTable]);

  const buildPeriodExportQueryString = useCallback((opts = {}) => {
    const params = {
      ...parseHeaderFiltersToParams(),
      toolbar_search: String(submittedSearchValueRef.current ?? '').trim() || undefined,
      sorters: JSON.stringify(cleanSorters(getTable()?.getSorters?.() ?? [])),
    };
    return buildExportQueryStringFromParams(params, opts);
  }, [getTable, parseHeaderFiltersToParams]);

  const runPeriodPrint = useCallback(async () => {
    try {
      setPrintChoiceVisible(false);
      setDialogText(t('Common.PreparingPrint'));
      setVisible(true);
      const suffix = buildPeriodExportQueryString({
        includePrintColumns: true,
        printLayout: printLayoutChoice,
        printOrientation: printOrientationChoice,
        printColumns: getPrintColumnsOrder(),
      });
      const html = await fetchTextGet(`/api/company_statistics/periods/export/print${suffix}`);
      if (!openPrintHtmlWindow(html)) toast?.current?.show({ severity: 'warn', summary: t('OtkCompanyStatisticsPeriodList.Toast.PrintTitle'), detail: t('Common.EnablePopups'), life: 4000 });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('OtkCompanyStatisticsPeriodList.Toast.PrintTitle'), detail: e?.message || t('OtkCompanyStatisticsPeriodList.Toast.PrintFailed'), life: 4500 });
    } finally {
      setVisible(false);
    }
  }, [buildPeriodExportQueryString, getPrintColumnsOrder, printLayoutChoice, printOrientationChoice, setDialogText, setVisible, t, toast]);

  const exportPeriodXlsx = useCallback(async () => {
    try {
      setDialogText(t('Common.PreparingExport'));
      setVisible(true);
      const suffix = buildPeriodExportQueryString({ includePrintColumns: true, printColumns: getPrintColumnsOrder() });
      const defaultXlsx = xlsxDownloadFilename(t(EXPORT_DOCUMENT_TITLE_KEYS.OTK_STATISTICS_BY_PERIOD));
      const { blob, filename } = await downloadBlobGet(`/api/company_statistics/periods/export/xlsx${suffix}`, defaultXlsx);
      triggerBlobDownload(blob, filename || defaultXlsx);
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('OtkCompanyStatisticsPeriodList.Toast.ExportTitle'), detail: e?.message || t('OtkCompanyStatisticsPeriodList.Toast.ExportFailed'), life: 4500 });
    } finally {
      setVisible(false);
    }
  }, [buildPeriodExportQueryString, getPrintColumnsOrder, setDialogText, setVisible, t, toast]);

  const closeMenu = () => {
    OverflowMenuProvider.setVisible(null);
    setExpandedKeys({});
  };

  const openMaskDialog = () => {
    setColumnsVisible(mergeVisibility(columnsVisible));
    setMaskVisible(true);
    closeMenu();
  };

  const saveUserMaskColumns = () => {
    const normalized = normalizePeriodColumnVisibilityForSave(columnsVisible);
    if (!Object.values(normalized).some(Boolean)) {
      toast?.current?.show({ severity: 'warn', summary: t('OtkCompanyStatisticsPeriodList.Toast.MaskWarnTitle'), detail: t('OtkCompanyStatisticsPeriodList.Toast.SelectOneColumn'), life: 4000 });
      return;
    }
    setUserMaskFilter(PAGE_ID, 'all', normalized, getTable()?.getHeaderFilters?.() ?? [], null, null);
    setColumnsVisible(normalized);
    setMaskVisible(false);
  };

  const confirmSaveStandardMask = () => {
    confirmDialog({
      message: t('OtkCompanyStatisticsPeriodList.SaveStandardMaskMessage'),
      header: t('OtkCompanyStatisticsPeriodList.SaveStandardMaskHeader'),
      icon: 'pi pi-info-circle',
      acceptLabel: t('Common.Save'),
      rejectLabel: t('Common.Cancel'),
      accept: () => {
        const layout = getTabulatorLayoutForStandardMask(getTable());
        if (!layout) return;
        setStandardMaskFilter(PAGE_ID, columnsFromTabulatorForStandardMaskSave(layout.columns), layout.attributes);
      },
    });
  };

  const confirmApplyStandardLayoutToUserMask = useCallback(() => {
    closeMenu();
    const standard = (state.standard_mask_filters || []).find((m) => m.page === PAGE_ID);
    if (!standard) {
      toast?.current?.show({ severity: 'warn', summary: t('OtkCompanyStatisticsPeriodList.Toast.StandardMaskWarnTitle'), detail: t('OtkCompanyStatisticsPeriodList.StandardMaskMissing'), life: 4000 });
      return;
    }
    const attributes = safeParse(standard.attributes, []);
    if (!Array.isArray(attributes) || attributes.length <= 1) return;
    const jsonColumns = safeParse(standard.columns, {});
    const merged = mergeVisibility(jsonColumns && typeof jsonColumns === 'object' ? jsonColumns : {});
    const colsForSave = normalizePeriodColumnVisibilityForSave(merged);
    confirmDialog({
      message: t('OtkCompanyStatisticsPeriodList.ApplyStandardMaskMessage'),
      header: t('OtkCompanyStatisticsPeriodList.ApplyStandardMaskHeader'),
      icon: 'pi pi-info-circle',
      acceptLabel: t('Common.Apply'),
      rejectLabel: t('Common.Cancel'),
      accept: () => {
        setUserMaskFilter(PAGE_ID, 'all', colsForSave, null, attributes, null, () => {
          const table = getTable();
          if (!table) return;
          isRestoringRef.current = true;
          try { table.setColumnLayout?.(attributes); } catch (_) {}
          setColumnsVisible(colsForSave);
          applyDataColumnVisibilityToTable(table, colsForSave);
          setTimeout(() => { isRestoringRef.current = false; }, 0);
        });
      },
    });
  }, [closeMenu, getTable, setUserMaskFilter, state.standard_mask_filters, t, toast]);

  function setMask(field, value) {
    if (!Object.prototype.hasOwnProperty.call(DEFAULT_COLUMNS_VISIBLE_PERIOD, field)) return;
    const next = { ...DEFAULT_COLUMNS_VISIBLE_PERIOD, ...columnsVisible, [field]: value };
    if (value === false && countVisibleDataColumns(next) < 1) {
      toast?.current?.show({ severity: 'warn', summary: t('OtkCompanyStatisticsPeriodList.Toast.MaskWarnTitle'), detail: t('OtkCompanyStatisticsPeriodList.Toast.AtLeastOneColumn'), life: 3500 });
      return;
    }
    applyDataColumnVisibilityToTable(getTable(), next);
    setColumnsVisible(next);
  }

  function maskSelectAll() {
    const next = { ...DEFAULT_COLUMNS_VISIBLE_PERIOD };
    Object.keys(next).forEach((k) => { next[k] = true; });
    applyDataColumnVisibilityToTable(getTable(), next);
    setColumnsVisible(next);
  }

  function maskDeselectAll() {
    const next = {};
    Object.keys(DEFAULT_COLUMNS_VISIBLE_PERIOD).forEach((k) => { next[k] = false; });
    next.period = true;
    next.year = true;
    applyDataColumnVisibilityToTable(getTable(), next);
    setColumnsVisible(next);
  }

  const itemRenderer = (item, options) => (
    <button type="button" className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px] border-0 text-left" onClick={options.onClick}>
      <span className={`${item.icon} text-primary`} />
      <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
    </button>
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (overlayRef.current && !overlayRef.current.contains(event.target)) setExpandedKeys({});
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, []);

  const items = useMemo(() => [{
    key: '0',
    label: t('Common.Actions'),
    icon: 'pi pi-sliders-v',
    template: itemRenderer,
    items: [
      { key: '0_1', label: t('Common.SetFilter'), icon: 'pi pi-filter', command: () => { setUserMaskFilter(PAGE_ID, 'set_filters', null, getTable()?.getHeaderFilters?.() ?? [], null); closeMenu(); }, template: itemRenderer },
      { key: '0_2', label: t('Common.ClearFilter'), icon: 'pi pi-filter-slash', command: () => { setUserMaskFilter(PAGE_ID, 'set_filters', null, [], null); getTable()?.clearHeaderFilter?.(); closeMenu(); }, template: itemRenderer },
      { key: '0_3', label: t('Common.SetSorting'), icon: 'pi pi-sort-alt', command: () => { setUserMaskFilter(PAGE_ID, 'set_sorters', null, null, null, cleanSorters(getTable()?.getSorters?.() ?? [])); closeMenu(); }, template: itemRenderer },
      { key: '0_4', label: t('Common.ClearSorting'), icon: 'pi pi-sort-alt-slash', command: () => { setUserMaskFilter(PAGE_ID, 'set_sorters', null, null, null, []); getTable()?.setSort?.([]); closeMenu(); }, template: itemRenderer },
      { key: '0_5', label: t('Common.SetMask'), icon: 'pi pi-server', command: openMaskDialog, template: itemRenderer },
      ...(hasApplicableStandardMask ? [{ key: '0_5s', label: t('Common.StandardMask'), icon: 'pi pi-clone', command: confirmApplyStandardLayoutToUserMask, template: itemRenderer }] : []),
      { key: '0_6', label: t('ServiceTask.Notifications'), icon: 'pi pi-envelope', command: () => { openChosenNotificationPreferences(PAGE_ID); closeMenu(); }, template: itemRenderer },
    ],
  }], [closeMenu, confirmApplyStandardLayoutToUserMask, getTable, hasApplicableStandardMask, openChosenNotificationPreferences, openMaskDialog, setUserMaskFilter, t]);

  const createNewPeriod = () => {
    dispatch({
      type: 'create_new_tab', name: t('OtkCompanyStatisticsPeriodList.Tab.ByPeriod'), translationKey: 'OtkCompanyStatisticsPeriodList.Tab.ByPeriod', legacyLabel: t('OtkCompanyStatisticsPeriodList.Tab.ByPeriod'),
      page: 'OtkCompanyStatisticsPeriodAdd',
      props: { year: '', month: '' },
      mask: 'gset023',
    });
  };

  const editSelected = () => {
    const row = getTable()?.getSelectedData?.()?.[0];
    if (row) onEditRow(row);
  };

  const searchTable = () => {
    const normalized = String(searchValue ?? '').trim();
    submittedSearchValueRef.current = normalized;
    setSearchIconColor(normalized === '' ? '' : 'active');
    applyToolbarSearchRowFilter();
    setVisibleSearch(false);
  };

  const scrollToFirst = () => scrollToFirstUtil(getTable());
  const scrollUp = () => scrollUpUtil(getTable());
  const scrollDown = () => scrollDownUtil(getTable());
  const scrollToLast = () => scrollToLastUtil(getTable());

  return (
    <>
      <ConfirmDialog />
      <div className="w-full h-10 flex flex-row bg-[#333]">
        <IntersectionObserverWrapper>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center">
            <button type="button" className="disabled:opacity-25" disabled={!user?.permissions?.includes('add settings')} onClick={createNewPeriod}><AddIcon /></button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center">
            <button type="button" className="disabled:opacity-25" disabled={editIcon} onClick={editSelected}><EditIcon /></button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center">
            <button type="button" title={t('Common.Print')} onClick={() => setPrintChoiceVisible(true)}><PrintIcon /></button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center">
            <button
              type="button"
              title={searchIconColor !== 'active' ? t('Common.Search') : t('ServiceTask.CancelSearch')}
              onClick={searchIconColor !== 'active' ? () => setVisibleSearch(true) : () => { setSearchIconColor(''); setSearchValue(''); submittedSearchValueRef.current = ''; applyToolbarSearchRowFilter(); }}
            >
              {searchIconColor === '' ? <MdSearch fill="#fff" className="w-[28px] h-[28px]" aria-hidden="true" /> : <MdSearchOff fill="#fff" className="w-[28px] h-[28px]" aria-hidden="true" />}
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center">
            <button type="button" title={t('Common.ExportToExcel')} onClick={exportPeriodXlsx}><ExportIcon /></button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center">
            <button type="button" onClick={reloadListData} title={t('Common.Refresh')} aria-label={t('Common.Refresh')}><RefreshIcon /></button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" title={t('Icon.First')} onClick={() => scrollToFirst()}><BackToFirstIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" title={t('Icon.Previous')} onClick={() => scrollUp()}><BackIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" title={t('Icon.Next')} onClick={() => scrollDown()}><ForwardIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" title={t('Icon.Last')} onClick={() => scrollToLast()}><ForwardToLastIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center">
            <button type="button" title={t('Common.AddToFavorites')} onClick={() => openFavoriteDialog(t('OtkCompanyStatisticsPeriodList.FavoriteTitle'), PAGE_ID)}><FavoriteIcon /></button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center">
            <button type="button" title={t('Common.QuickView')} onClick={() => setFastView(PAGE_ID, columnsVisible, columns)}><PriceIcon /></button>
          </div>
          {canSetStandardMask ? (
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center">
              <button type="button" title={t('Common.SaveStandardMask')} aria-label={t('Common.SaveStandardMask')} onClick={confirmSaveStandardMask} className="flex items-center justify-center">
                <i className="pi pi-bookmark text-white text-xl" aria-hidden="true" />
              </button>
            </div>
          ) : null}
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
          <div className="card flex justify-content-center z-[100]" ref={overlayRef}>
            <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem" />
          </div>
        </IntersectionObserverWrapper>
      </div>

      <div className="px-1 bg-white rounded-md shadow-md">
        <div className="flex flex-col overflow-x-auto no-scrollbar min-h-[calc(100vh-177px)]">
          <div className="overflow-hidden">
            <div className="inline-block w-full py-2">
              <ReactTabulator
                key="otk-company-statistics-period-list"
                data={periods}
                className="h-[calc(100vh_-_193px)]"
                onRef={(r) => { ref.current = r?.current; }}
                columns={columns}
                events={{
                  rowSelectionChanged: onSelectRow,
                  tableBuilt: () => {
                    const table = getTable();
                    if (!table) return;
                    const effective = getEffectiveMask(PAGE_ID, state);
                    if (effective?.type === 'user' && effective.userMask) {
                      isRestoringRef.current = true;
                      applyUserMaskRowToTabulator(table, effective.userMask, {
                        setColumnsVisible: (v) => setColumnsVisible(mergeVisibility(v)),
                        clearHeaderFiltersWhenEmpty: true,
                        applyColumnVisibilityFn: (tbl, jc, setVis) => {
                          const merged = mergeVisibility(jc);
                          setVis(merged);
                          applyDataColumnVisibilityToTable(tbl, merged);
                        },
                      });
                      setTimeout(() => { isRestoringRef.current = false; }, 0);
                    } else if (effective?.type === 'standard' && effective.standardMask) {
                      isRestoringRef.current = true;
                      const attributes = safeParse(effective.standardMask.attributes, []);
                      const jsonColumns = safeParse(effective.standardMask.columns, {});
                      if (Array.isArray(attributes) && attributes.length > 1) table.setColumnLayout?.(attributes);
                      if (jsonColumns && typeof jsonColumns === 'object') {
                        const merged = mergeVisibility(jsonColumns);
                        setColumnsVisible(merged);
                        applyDataColumnVisibilityToTable(table, merged);
                      }
                      setTimeout(() => { isRestoringRef.current = false; }, 0);
                    } else {
                      applyDataColumnVisibilityToTable(table, DEFAULT_COLUMNS_VISIBLE_PERIOD);
                    }
                    setTimeout(() => applyToolbarSearchRowFilter(), 0);
                  },
                  ...getTabulatorPersistLayoutEvents(PAGE_ID, ref, setUserMaskFilter),
                }}
                options={{ renderHorizontal: 'virtual', layout: 'fitColumns', movableColumns: true, autoResize: true, headerSortTristate: true, filterMode: 'local' }}
              />
            </div>
          </div>
        </div>
      </div>

      <PrintOptionsDialog visible={printChoiceVisible} onHide={() => setPrintChoiceVisible(false)} orientation={printOrientationChoice} onOrientationChange={setPrintOrientationChoice} layout={printLayoutChoice} onLayoutChange={setPrintLayoutChoice} onConfirm={runPeriodPrint} />

      <Dialog header={t('OtkCompanyStatisticsPeriodList.SearchDialogTitle')} visible={visibleSearch} style={{ width: '40vw' }} onHide={() => setVisibleSearch(false)}>
        <div className="flex flex-col">
          <p className="text-sm text-gray-600 mb-2">{t('OtkCompanyStatisticsPeriodList.SearchHint')}</p>
          <div className="flex items-center gap-3 w-full">
            <input value={searchValue} onChange={(e) => setSearchValue(e.target.value)} type="text" className="flex-1 h-10 px-4 border rounded-md focus:border-gray-400" />
            <button type="button" onClick={searchTable} className="h-10 w-16 bg-zinc-900 text-white rounded-md hover:bg-zinc-800 flex items-center justify-center"><SearchIcon /></button>
          </div>
        </div>
      </Dialog>

      <Dialog header={t('OtkCompanyStatisticsPeriodList.MaskDialogTitle')} visible={maskVisible} style={{ width: '70vw' }} onHide={() => setMaskVisible(false)}>
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-2 m-1">
            <button type="button" onClick={maskSelectAll} className="px-3 py-1.5 text-sm bg-zinc-200 text-zinc-900 rounded-md hover:bg-zinc-300">{t('OtkCompanyStatisticsPeriodList.MaskSelectAll')}</button>
            <button type="button" onClick={maskDeselectAll} className="px-3 py-1.5 text-sm bg-zinc-200 text-zinc-900 rounded-md hover:bg-zinc-300">{t('OtkCompanyStatisticsPeriodList.MaskDeselectAll')}</button>
          </div>
          {Object.keys(DEFAULT_COLUMNS_VISIBLE_PERIOD).map((field) => (
            <div key={field} className="flex flex-row border border-black rounded-md p-2 m-1">
              <Checkbox inputId={`otk-period-mask-${field}`} onChange={(e) => setMask(field, e.checked)} checked={columnsVisible[field] !== false} />
              <label htmlFor={`otk-period-mask-${field}`} className="ml-2 cursor-pointer">{getMaskColumnLabel(field)}</label>
            </div>
          ))}
          <button type="button" onClick={saveUserMaskColumns} className="m-1 px-4 py-2 text-center tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600">{t('OtkCompanyStatisticsPeriodList.MaskSave')}</button>
        </div>
      </Dialog>
    </>
  );
}
